import { FC } from "react";
import { Stack, Text } from "@mantine/core";

import { Issue } from "@protos/issues/v1/issues";
import { Reachability } from "@protos/sca/v1/sca";
import { InlineMarkdownCode } from "@shared/components";
import { formatTextAsMarkdown } from "@shared/utils";

export interface ReachabilityInfoProps {
  finding: Issue;
}

export const ReachabilityInfo: FC<ReachabilityInfoProps> = ({ finding }) => {
  const reachability = finding.scaAttributes?.reachability;
  const reachableCondition = finding.scaAttributes?.reachableCondition ?? "";

  if (reachability === Reachability.REACHABLE) {
    return null;
  }

  return (
    <Stack gap="xs">
      <Text size="md" fw={500}>
        Reachability Details
      </Text>
      {reachability === Reachability.CONDITIONALLY_REACHABLE && (
        <InlineMarkdownCode
          text={
            "This vulnerability is reachable if " +
            formatTextAsMarkdown(reachableCondition)
          }
        />
      )}
      {reachability === Reachability.ALWAYS_REACHABLE && (
        <Text>
          This finding is considered "always reachable" because Semgrep
          identifies the {finding.scaAttributes?.foundDependency?.package}{" "}
          package itself as the vulnerability, regardless of its actual usage.
          Fixing requires upgrading or replacing the dependency.
        </Text>
      )}
      {reachability === Reachability.UNREACHABLE && (
        <Text>
          Semgrep did not find code related to this vulnerability in{" "}
          {finding.repository?.name}.
        </Text>
      )}
      {(reachability === Reachability.UNKNOWN ||
        reachability === Reachability.UNSPECIFIED) && (
        <Text>No reachability analysis was done for this vulnerability.</Text>
      )}
    </Stack>
  );
};
